import React, { useState, useEffect } from 'react';
import { db } from '../firebase/firestore';
import { collection, getDocs, query, where, orderBy, addDoc } from 'firebase/firestore';
import ImageUploader from "./ImageUploader";

const AdminCatalogForm = ({ 
  productData, 
  loading,
  message,
  isEditing,
  onProductChange,
  onFeatureChange,
  onAddFeature,
  onRemoveFeature,
  onColorChange,
  onAddColor,
  onRemoveColor,
  onSubmit,
  onCancel
}) => {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Загрузка категорий из Firebase при монтировании
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const categoriesSnapshot = await getDocs(
        query(
          collection(db, "categories"),
          where("isActive", "==", true),
          orderBy("order")
        )
      );
      
      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      
      setCategories(categoriesData);
    } catch (err) {
      console.error('Ошибка при загрузке категорий:', err);
      // Если ошибка, показываем пустой список
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Функция для добавления новой категории в базу данных
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      alert('Введите название категории');
      return;
    }

    try {
      // Добавляем категорию в Firestore
      const newCategoryRef = await addDoc(collection(db, "categories"), {
        name: newCategoryName.trim(),
        isActive: true,
        order: categories.length,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Добавляем новую категорию в локальный список
      const newCategory = {
        id: newCategoryRef.id,
        name: newCategoryName.trim()
      };
      
      setCategories(prev => [...prev, newCategory]);
      
      // Автоматически выбираем новую категорию для товара
      onProductChange({
        target: {
          name: 'category',
          value: newCategoryRef.id
        }
      });
      
      setNewCategoryName('');
      setShowCategoryPopup(false);
      
      alert('Категория успешно добавлена и выбрана!');
    } catch (err) {
      console.error('Ошибка при добавлении категории:', err);
      alert('Ошибка при добавлении категории');
    }
  };

  // Проверяем, есть ли категории для отображения
  const hasCategories = categories.length > 0;

  // Получаем название выбранной категории
  const getSelectedCategoryName = () => {
    if (!productData.category || productData.category === 'Каталог тканей') {
      return 'Каталог тканей';
    }

    const category = categories.find(c => c.id === productData.category);
    return category?.name || 'Каталог тканей';
  };

  // Обработчик для удаления изображения
  const handleRemoveImage = (index) => {
    const updatedImages = (productData.images || []).filter((_, i) => i !== index);
    onProductChange({
      target: {
        name: 'images',
        value: updatedImages
      }
    });
  };

  // Обработчик для изменения порядка изображений
  const handleMoveImage = (index, direction) => {
    const images = productData.images || [];
    if (index === 0 && direction === -1) return;
    if (index === images.length - 1 && direction === 1) return;
    
    const newIndex = index + direction;
    const updatedImages = [...images];
    const temp = updatedImages[index];
    updatedImages[index] = updatedImages[newIndex];
    updatedImages[newIndex] = temp;
    
    onProductChange({
      target: {
        name: 'images',
        value: updatedImages
      }
    });
  };

  // Обработчик для редактирования URL конкретного изображения
  const handleEditImageUrl = (index) => {
    const currentUrl = productData.images?.[index] || '';
    const newUrl = prompt('Введите новый URL изображения:', currentUrl);
    
    if (newUrl !== null && newUrl.trim() !== currentUrl) {
      const updatedImages = [...(productData.images || [])];
      updatedImages[index] = newUrl.trim();
      
      onProductChange({
        target: {
          name: 'images',
          value: updatedImages
        }
      });
    }
  };

  // Обработчик для добавления нового изображения через ImageUploader
  const handleAddImage = (imageUrl) => {
    const updatedImages = [...(productData.images || []), imageUrl];
    onProductChange({
      target: {
        name: 'images',
        value: updatedImages
      }
    });
  };

  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border border-gray-700 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">
          {isEditing ? 'Редактировать товар' : 'Добавить товар'}
        </h2>
        {onCancel && (
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();

          const normalizedCategory =
            productData.category && productData.category.trim()
              ? productData.category
              : 'Каталог тканей';

          onProductChange({
            target: {
              name: 'category',
              value: normalizedCategory,
            },
          });

          // даём React обновить state и только потом сабмитим
          setTimeout(() => {
            onSubmit(e);
          }, 0);
        }}
      >

        {/* Сообщение об успехе/ошибке */}
        {message && (
          <div className={`mb-4 p-3 rounded-lg ${message.includes('успешно') ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>
            {message}
          </div>
        )}

        {/* Основная информация */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Название товара *</label>
            <input
              type="text"
              name="name"
              value={productData.name}
              onChange={onProductChange}
              className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
              placeholder="Льняная скатерть 'Noble'"
              required
            />
          </div>
          
          {/* Категория с возможностью добавления новой */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-300">Категория</label>
              <button
                type="button"
                onClick={() => setShowCategoryPopup(true)}
                className="text-xs px-2 py-1 bg-gradient-to-r from-green-600 to-emerald-500 rounded-lg hover:opacity-90 transition-opacity"
              >
                + Новая категория
              </button>
            </div>
            
            {categoriesLoading ? (
              <div className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg">
                <div className="flex items-center text-gray-400">
                  <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                  Загрузка категорий...
                </div>
              </div>
            ) : hasCategories ? (
              <>
                <select
                  name="category"
                  value={productData.category || ''}
                  onChange={onProductChange}
                  className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                >
                  <option value="">-- Без категории (будет "Каталог тканей") --</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <div className="mt-2">
                  <span className="text-xs text-gray-500">
                    Выбрано: {getSelectedCategoryName()}
                  </span>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <div className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-gray-400">
                  Нет доступных категорий. Товар будет добавлен в "Каталог тканей".
                </div>
                <button
                  type="button"
                  onClick={() => setShowCategoryPopup(true)}
                  className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-500 text-white font-bold rounded-lg hover:opacity-90 transition-opacity"
                >
                  Создать новую категорию
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Попап для добавления новой категории */}
        {showCategoryPopup && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-white">Добавить новую категорию</h3>
                  <button
                    onClick={() => setShowCategoryPopup(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Название новой категории *
                  </label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
                    placeholder="Например: Скатерти"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Категория появится в списке выбора и на странице каталога
                  </p>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCategoryPopup(false)}
                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-500 text-white font-bold rounded-lg hover:opacity-90 transition-opacity"
                  >
                    Добавить категорию
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Описание */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">Описание *</label>
          <textarea
            name="description"
            value={productData.description}
            onChange={onProductChange}
            rows="3"
            className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
            placeholder="Плотная льняная ткань с антибактериальной пропиткой..."
            required
          />
        </div>

        {/* Видео */}
        <div className="mb-6 bg-gray-800/30 p-4 rounded-xl border border-gray-700">
          <h3 className="text-lg font-medium text-gray-300 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"/>
            </svg>
            Видео (опционально)
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Ссылка на видео</label>
              <input
                type="url"
                name="videoUrl"
                value={productData.videoUrl}
                onChange={onProductChange}
                className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                placeholder="https://example.com/video.mp4"
              />
              <p className="text-xs text-gray-500 mt-1">Поддерживаемые форматы: MP4, WebM</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Длительность видео</label>
              <input
                type="text"
                name="videoDuration"
                value={productData.videoDuration}
                onChange={onProductChange}
                className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                placeholder="2:45"
              />
              <p className="text-xs text-gray-500 mt-1">Формат: мм:сс (например: 1:30)</p>
            </div>
          </div>
          
          {productData.videoUrl && (
            <div className="mt-2 p-2 bg-blue-900/20 rounded-lg">
              <div className="flex items-center text-sm text-blue-300">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd"/>
                </svg>
                Видео будет отображаться на карточке товара
              </div>
            </div>
          )}
        </div>

        {/* Особенности */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-300">Особенности</label>
            <button
              type="button"
              onClick={onAddFeature}
              className="px-3 py-1 bg-gray-700 rounded-lg text-sm hover:bg-gray-600"
            >
              + Добавить
            </button>
          </div>
          {productData.features.map((feature, index) => (
            <div key={index} className="flex gap-2 mb-2">
              <input
                type="text"
                value={feature}
                onChange={(e) => onFeatureChange(index, e.target.value)}
                className="flex-1 p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                placeholder="Антибактериальная"
              />
              <button
                type="button"
                onClick={() => onRemoveFeature(index)}
                className="px-4 py-3 bg-red-500/20 text-red-300 rounded-lg hover:bg-red-500/30"
              >
                Удалить
              </button>
            </div>
          ))}
        </div>

       

        {/* Спецификации */}
        <div className="mb-6">
          <label className="block text-lg font-medium text-gray-300 mb-4">Спецификации</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Плотность</label>
              <input
                type="text"
                name="specifications.threadCount"
                value={productData.specifications.threadCount}
                onChange={onProductChange}
                className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                placeholder="-"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Вес</label>
              <input
                type="text"
                name="specifications.weight"
                value={productData.specifications.weight}
                onChange={onProductChange}
                className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                placeholder="220 г/м²"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Материал</label>
              <input
                type="text"
                name="specifications.material"
                value={productData.specifications.material}
                onChange={onProductChange}
                className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white"
                placeholder="100% лён"
              />
            </div>
          </div>
        </div>

        {/* Несколько изображений через ImageUploader */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <label className="block text-lg font-medium text-gray-300">
              Изображения товара *
            </label>
            <span className="text-sm text-gray-400">
              {(productData.images || []).length} из 10 загружено
            </span>
          </div>
          
          {/* Компонент ImageUploader */}
          <div className="mb-4">
            <ImageUploader
              label="Добавить изображение товара"
              hint="Рекомендуется 800x800px. Загрузите файл или введите URL"
              disabled={(productData.images || []).length >= 10}
              onUpload={handleAddImage}
              buttonText={"+ Добавить изображение"}
              showUrlInput={true}
            />
            <div className="text-sm text-gray-500 space-y-1 mt-2">
              <p>• Нажмите кнопку "Добавить изображение" для загрузки файла или ввода URL</p>
              <p>• Поддерживаются JPG, PNG, WebP форматы</p>
              <p>• Максимум 10 изображений. Первое изображение - основное.</p>
            </div>
          </div>
          
          {/* Предпросмотр изображений */}
          {(productData.images || []).length > 0 ? (
            <div className="space-y-4">
              <div className="text-sm text-gray-400">
                Первое изображение будет использоваться как основное. Используйте кнопки ↑↓ для изменения порядка.
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(productData.images || []).map((image, index) => (
                  <div key={index} className="relative group bg-gray-800/30 rounded-xl p-3 border border-gray-700">
                    <div className={`relative rounded-lg overflow-hidden border-2 ${index === 0 ? 'border-amber-500' : 'border-gray-600'}`}>
                      <img
                        src={image}
                        alt={`Товар ${index + 1}`}
                        className="w-full h-40 object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiMzMzMiLz48cGF0aCBkPSJNNjAgNDBMNDAgODBINDBMODAgNDBIMTAwTDEwMCA4MEg4MEw2MCA0MFoiIGZpbGw9IiM2NjYiLz48L3N2Zz4=';
                          e.target.alt = 'Ошибка загрузки изображения';
                        }}
                      />
                      
                      {index === 0 && (
                        <div className="absolute top-2 left-2 bg-amber-500 text-white text-xs px-2 py-1 rounded">
                          Основное
                        </div>
                      )}
                      
                      {/* Кнопки управления */}
                      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                        <button
                          type="button"
                          onClick={() => handleMoveImage(index, -1)}
                          disabled={index === 0}
                          className={`p-2 rounded-full ${index === 0 ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed' : 'bg-white/20 text-white hover:bg-white/30'}`}
                          title="Переместить вверх"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditImageUrl(index)}
                          className="p-2 rounded-full bg-blue-500/80 text-white hover:bg-blue-600"
                          title="Изменить URL"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="p-2 rounded-full bg-red-500/80 text-white hover:bg-red-600"
                          title="Удалить"
                        >
                          ×
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveImage(index, 1)}
                          disabled={index === (productData.images || []).length - 1}
                          className={`p-2 rounded-full ${index === (productData.images || []).length - 1 ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed' : 'bg-white/20 text-white hover:bg-white/30'}`}
                          title="Переместить вниз"
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-400">
                          Изображение {index + 1}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleEditImageUrl(index)}
                        className="w-full mt-1 px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded transition-colors flex items-center justify-center"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Изменить URL
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-8 border-2 border-dashed border-gray-700 rounded-xl text-center">
              <svg className="w-12 h-12 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-400 mb-2">
                Нет загруженных изображений
              </p>
              <p className="text-sm text-gray-500">
                Нажмите кнопку "Добавить изображение" выше
              </p>
            </div>
          )}
        </div>

        {/* Кнопки в форме */}
        <div className="flex justify-end space-x-4">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 bg-gray-700 text-white font-bold rounded-lg hover:bg-gray-600 transition-colors"
            >
              Отмена
            </button>
          )}
          <button
            type="submit"
            disabled={loading || (productData.images?.length || 0) === 0}
            className="px-8 py-3 bg-gradient-to-r from-amber-600 to-orange-500 text-white font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Сохранение...' : (isEditing ? 'Сохранить товар' : 'Добавить товар')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminCatalogForm;